import React from 'react';
import {
  IconButton,
  Stack,
  Tag,
  TabGroup,
  Tab,
  TabPanel,
  TextTitle,
  sendToast,
} from '@pypestream/design-system';
import { StudioResourceTypeEnum } from '@pypestream/api-services';

import { ConfigPanel } from '../src/config-panel';
import { tableStore } from '../../../stores/table-store';
import { useBobcatFlag, useUploadPythonFiles } from '../../../hooks';
import { ResourceLibItem } from '../../../xstate/prostudio.xstate.types';
import {
  AGENT_TEMPLATES_CATEGORY,
  MICROAGENT_CATEGORIES,
  UNCATEGORIZED_CATEGORY,
  categoryIdOf,
} from './category-map';
import { LibraryCard } from './library-card';
import { matchesLibrarySearch } from './library-search';
import { MicroagentCategories } from './microagent-categories';
import { MicroagentCategoryDetail } from './microagent-category-detail';
import { ActionNodesList } from './action-nodes-list';
import { systemActionNodeItems } from './system-action-nodes';
import { agentTemplateItems } from './agent-template-items';
import {
  scriptNamesFor,
  uploadResourceLibScripts,
} from './upload-resource-lib-scripts';

import './resource-lib.scss';

export const ResourceLibPanel: React.FC<{
  viewOnly?: boolean;
  resourceLibData?: ResourceLibItem[];
  onClose: () => void;
}> = ({ resourceLibData, onClose, viewOnly }) => {
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<
    string | null
  >(null);
  const [isResourceLibAdding, setIsResourceLibAdding] = React.useState(false);
  const [microagentQuery, setMicroagentQuery] = React.useState('');
  // Project Bobcat (bobcat-resource-library-drag-drop): the prototype's
  // library cards, rows and search. Off, the panel is unchanged.
  const polished = useBobcatFlag('resourceLibraryDragDrop') === 'allowed';
  const { handleUpload } = useUploadPythonFiles('scriptFile');

  // Project Bobcat (bobcat-agent-templates): the agent templates, first.
  const agentTemplatesOn = useBobcatFlag('agentTemplates') === 'allowed';
  const microagentItems = React.useMemo(
    () => [
      ...(agentTemplatesOn ? agentTemplateItems() : []),
      ...(resourceLibData || []).filter(
        (item) => item.type === StudioResourceTypeEnum.MicroAgent
      ),
    ],
    [resourceLibData, agentTemplatesOn]
  );
  // The catalog's action nodes, then the platform's built-in ones.
  const { actionNodeNames } = tableStore;
  const actionNodeItems = React.useMemo(() => {
    const catalog = (resourceLibData || []).filter(
      (item) => item.type === StudioResourceTypeEnum.ActionNode
    );
    return [...catalog, ...systemActionNodeItems(actionNodeNames, catalog)];
  }, [resourceLibData, actionNodeNames]);

  const categoryCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    microagentItems.forEach((item) => {
      const categoryId = categoryIdOf(item);
      counts[categoryId] = (counts[categoryId] || 0) + 1;
    });
    return counts;
  }, [microagentItems]);

  const selectedCategory = [
    AGENT_TEMPLATES_CATEGORY,
    ...MICROAGENT_CATEGORIES,
    UNCATEGORIZED_CATEGORY,
  ].find((category) => category.id === selectedCategoryId);
  const microagentMatches = microagentQuery.trim()
    ? microagentItems.filter((item) =>
        matchesLibrarySearch(item, microagentQuery)
      )
    : null;
  const selectedCategoryItems = selectedCategoryId
    ? microagentItems.filter(
        (item) => categoryIdOf(item) === selectedCategoryId
      )
    : [];

  const handleAddResourceLib = async (item: ResourceLibItem) => {
    setIsResourceLibAdding(true);

    if (!item.data) {
      return;
    }

    try {
      await uploadResourceLibScripts(
        scriptNamesFor(item.data),
        handleUpload,
        item.data
      );

      tableStore.addResourceLibRows(item.data);
    } catch (e) {
      console.error(e);

      sendToast({
        type: 'error',
        text: 'Failed to add Resource Library',
        autoClose: 3000,
      });
    } finally {
      setIsResourceLibAdding(false);
    }
  };

  const selectItem = (item: ResourceLibItem) => {
    if (viewOnly) {
      return;
    }

    handleAddResourceLib(item);
    onClose();
  };

  return (
    <ConfigPanel
      title={
        selectedCategory ? (
          <Stack nowrap gutter="xsmall" alignItems="center">
            <IconButton
              name="arrow-left"
              size="small"
              onClick={() => setSelectedCategoryId(null)}
            />
            <TextTitle size="xsmall">{selectedCategory.name}</TextTitle>
          </Stack>
        ) : (
          <Stack nowrap gutter="xsmall" alignItems="center">
            <TextTitle size="xsmall">Resource Library</TextTitle>
            <Tag minimal variant="indicator">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 8.0001H8.00667M10.357 10.3571C7.23283 13.4813 3.64489 14.9587 2.34315 13.6569C1.0414 12.3552 2.51878 8.76727 5.64298 5.64307C8.76717 2.51888 12.3551 1.04149 13.6569 2.34324C14.9586 3.64499 13.4812 7.23292 10.357 10.3571ZM10.357 5.64295C13.4812 8.76715 14.9586 12.3551 13.6568 13.6568C12.3551 14.9586 8.76715 13.4812 5.64295 10.357C2.51876 7.2328 1.04137 3.64487 2.34312 2.34312C3.64487 1.04137 7.2328 2.51876 10.357 5.64295ZM8.33333 8.0001C8.33333 8.18419 8.1841 8.33343 8 8.33343C7.81591 8.33343 7.66667 8.18419 7.66667 8.0001C7.66667 7.816 7.81591 7.66676 8 7.66676C8.1841 7.66676 8.33333 7.816 8.33333 8.0001Z"
                  stroke="#5956E3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Beta
            </Tag>
          </Stack>
        )
      }
      onClose={onClose}
    >
      {selectedCategory ? (
        <MicroagentCategoryDetail
          items={selectedCategoryItems}
          disabled={isResourceLibAdding || viewOnly}
          onSelectItem={selectItem}
        />
      ) : (
        <TabGroup className="c-resource-lib__tab-group">
          <Tab slot="tab" panel="microagents">
            Microagents
          </Tab>
          <Tab slot="tab" panel="action-nodes">
            Action Nodes
          </Tab>
          <TabPanel name="microagents">
            {polished && (
              <div className="c-resource-lib__sticky-search c-resource-lib__sticky-search--action-nodes">
                <input
                  type="search"
                  className="c-lib-search"
                  placeholder="Search microagents…"
                  aria-label="Search microagents"
                  data-testid="bobcat-library-microagent-search"
                  value={microagentQuery}
                  onChange={(e) => setMicroagentQuery(e.target.value)}
                />
              </div>
            )}
            {polished && microagentMatches ? (
              microagentMatches.length === 0 ? (
                <p className="c-lib-empty">
                  No microagents match “{microagentQuery.trim()}”.
                </p>
              ) : (
                <div className="c-lib-list">
                  {microagentMatches.map((item) => (
                    <LibraryCard
                      key={item.id}
                      item={item}
                      kind="microagent"
                      disabled={isResourceLibAdding || viewOnly}
                      dragEnabled={!viewOnly}
                      onSelect={selectItem}
                    />
                  ))}
                </div>
              )
            ) : (
              <MicroagentCategories
                counts={categoryCounts}
                onSelect={setSelectedCategoryId}
                polished={polished}
              />
            )}
          </TabPanel>
          <TabPanel name="action-nodes">
            <ActionNodesList
              items={actionNodeItems}
              disabled={isResourceLibAdding || viewOnly}
              onSelectItem={selectItem}
            />
          </TabPanel>
        </TabGroup>
      )}
    </ConfigPanel>
  );
};
